import React from 'react';
import { Search, Calendar } from 'lucide-react';
import { Input } from '../../../ui/input';

interface FilterOption {
    value: string;
    label: string;
}

interface SearchAndFiltersProps {
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder?: string;
    filters?: {
        value: string;
        onChange: (value: string) => void;
        options: FilterOption[];
        label?: string;
    }[];
    dateFilter?: string;
    customDateRange?: { start: string; end: string };
    onCustomDateRangeChange?: (range: { start: string; end: string }) => void;
}

export function SearchAndFilters({
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Search...',
    filters = [],
    dateFilter,
    customDateRange,
    onCustomDateRangeChange
}: SearchAndFiltersProps) {
    return (
        <div className="glass-strong rounded-xl border border-primary/30 p-6 space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={searchPlaceholder}
                        className="pl-10 glass border-border/30"
                    />
                </div>
                {filters.length > 0 && (
                    <div className="flex gap-4 flex-wrap">
                        {filters.map((filter, index) => (
                            <select
                                key={index}
                                value={filter.value}
                                onChange={(e) => filter.onChange(e.target.value)}
                                className="px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background"
                                aria-label={filter.label}
                            >
                                {filter.options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Custom Date Range Picker */}
            {dateFilter === 'custom' && onCustomDateRangeChange && (
                <div className="flex items-center gap-4 p-4 rounded-lg glass border border-primary/30">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div className="flex items-center gap-2 flex-1">
                        <div className="flex-1">
                            <label className="text-sm text-muted-foreground mb-1 block">Start Date</label>
                            <Input
                                type="date"
                                value={customDateRange?.start || ''}
                                onChange={(e) => onCustomDateRangeChange({ ...customDateRange!, start: e.target.value })}
                                className="glass border-border/30"
                            />
                        </div>
                        <span className="text-muted-foreground mt-6">to</span>
                        <div className="flex-1">
                            <label className="text-sm text-muted-foreground mb-1 block">End Date</label>
                            <Input
                                type="date"
                                value={customDateRange?.end || ''}
                                onChange={(e) => onCustomDateRangeChange({ ...customDateRange!, end: e.target.value })}
                                className="glass border-border/30"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}